import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import IncidentValidationSchema from './IncidentValidationSchema';
import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { Incident, IncidentStatus } from '../../core/api/clients';

import ControlledForm from '../../components/ControlledForm';

import IncidentFormBanner from '../../components/incident/IncidentFormBanner';
import IncidentFormBody from '../../components/incident/IncidentFormBody';

import { useIncidentReportData } from '../../core/context/IncidentReportDataContext';
import { FormValidationProvider } from '../../core/context/FormValidationContext';

function IncidentFormEdit(): React.JSX.Element {
  const { incidentReport, ...incidentReportMethods } = useIncidentReportData();
  const { metadata } = useAuthorizedFormData();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const {
    formState: {
      isDirty,
      dirtyFields,
      ...formStateRest
    },
    ...formMethods
  } = useForm<Incident>({
    defaultValues: incidentReport,
    mode: 'onBlur',
    resolver: yupResolver(IncidentValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(incidentReport);
  }, [incidentReport]);

  const handleSave = () => incidentReportMethods.update({ ...formMethods.getValues() });

  const onSubmit = () => {
    if (isDirty) {
      enqueueSnackbar('You have un-saved data', { variant: 'error' });
    } else {
      incidentReportMethods.closeIncident(formMethods.getValues(), () => navigate(`/form/incident/${incidentReport.id}/view`));
    }
  };
  const handleDelete = () => {
    incidentReportMethods.deleteIncident(() => {
      navigate(`${metadata?.route}`);
    });
  };
  const onError = () => null;

  return (
    <ControlledForm
      enableUpdate={incidentReport.status === IncidentStatus.Open}
      formProps={{
        noValidate: true,
      }}
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
    >
      <FormProvider formState={{ isDirty, dirtyFields, ...formStateRest }} {...formMethods}>
        <FormValidationProvider<Incident>>
          <IncidentFormBanner
            incident={incidentReport}
            saveButtonProps={{
              isDirty,
              onSave: handleSave,
              saveStatus: incidentReportMethods.updateStatus,
              lastSaved: incidentReport?.lastModifiedBy === 'System' ? null : incidentReport?.lastModifiedOn,
            }}
          />
          <IncidentFormBody incident={incidentReport} onDelete={handleDelete} />
        </FormValidationProvider>
      </FormProvider>
    </ControlledForm>
  );
}

export default IncidentFormEdit;
